import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { getToken, getUser } from "@/lib/auth";
import DashboardLayout from "@/components/layout/DashboardLayout";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import SWUpdateNotification from "@/components/SWUpdateNotification";
import "@/i18n";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import EmployeesPage from "@/pages/EmployeesPage";
import EmployeeProfilePage from "@/pages/EmployeeProfilePage";
import AttendancePage from "@/pages/AttendancePage";
import LeavePage from "@/pages/LeavePage";
import PayrollPage from "@/pages/PayrollPage";
import DocumentsPage from "@/pages/DocumentsPage";
import ReportsPage from "@/pages/ReportsPage";
import PerformancePage from "@/pages/PerformancePage";
import AnnouncementsPage from "@/pages/AnnouncementsPage";
import HolidaysPage from "@/pages/HolidaysPage";
import HRLettersPage from "@/pages/HRLettersPage";
import HRPolicyPage from "@/pages/HRPolicyPage";
import TeamMasterPage from "@/pages/TeamMasterPage";
import AuditLogsPage from "@/pages/AuditLogsPage";
import SelfServicePage from "@/pages/SelfServicePage";
import JourneyPlanPage from "@/pages/JourneyPlanPage";
import EmailTemplatesPage from "@/pages/EmailTemplatesPage";
import UserManagementPage from "@/pages/UserManagementPage";
import ForceChangePasswordPage from "@/pages/ForceChangePasswordPage";
import MobileClockPage from "@/pages/MobileClockPage";
import NotificationPreferencesPage from "@/pages/NotificationPreferencesPage";
import OnboardingPage from "@/pages/OnboardingPage";
import AssetsPage from "@/pages/AssetsPage";
import ExitFormPage from "@/pages/ExitFormPage";

function ProtectedRoute({ children }) {
  const user = getUser();
  if (!getToken()) return <Navigate to="/login" replace />;
  if (user?.must_change_password) return <Navigate to="/change-password" replace />;
  return children;
}

function AdminRoute({ children }) {
  const user = getUser();
  if (!user || !["admin", "super_admin", "hr"].includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function ChangePasswordRoute({ children }) {
  if (!getToken()) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/change-password" element={<ChangePasswordRoute><ForceChangePasswordPage /></ChangePasswordRoute>} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="employees" element={<AdminRoute><EmployeesPage /></AdminRoute>} />
          <Route path="employees/:id" element={<EmployeeProfilePage />} />
          <Route path="attendance" element={<AttendancePage />} />
          <Route path="leave" element={<LeavePage />} />
          <Route path="payroll" element={<PayrollPage />} />
          <Route path="documents" element={<DocumentsPage />} />
          <Route path="reports" element={<AdminRoute><ReportsPage /></AdminRoute>} />
          <Route path="performance" element={<PerformancePage />} />
          <Route path="announcements" element={<AnnouncementsPage />} />
          <Route path="holidays" element={<HolidaysPage />} />
          <Route path="hr-letters" element={<HRLettersPage />} />
          <Route path="hr-policy" element={<HRPolicyPage />} />
          <Route path="journey-plans" element={<JourneyPlanPage />} />
          <Route path="team-master" element={<AdminRoute><TeamMasterPage /></AdminRoute>} />
          <Route path="audit-logs" element={<AdminRoute><AuditLogsPage /></AdminRoute>} />
          <Route path="email-templates" element={<AdminRoute><EmailTemplatesPage /></AdminRoute>} />
          <Route path="user-management" element={<AdminRoute><UserManagementPage /></AdminRoute>} />
          <Route path="clock" element={<MobileClockPage />} />
          <Route path="notifications" element={<NotificationPreferencesPage />} />
          <Route path="onboarding" element={<OnboardingPage />} />
          <Route path="assets" element={<AssetsPage />} />
          <Route path="exit-form" element={<ExitFormPage />} />
          <Route path="my-profile" element={<SelfServicePage />} />
        </Route>
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
      <Toaster position="top-right" richColors />
      <PWAInstallPrompt />
      <SWUpdateNotification />
    </BrowserRouter>
  );
}

export default App;
